/**
 * Comparativo — tela 7 do funil Premium: Grátis × Premium.
 *
 * Fonte: handoffAnuncios/PROMPT-claude-code.md § 7 e Monetizacao.dc.html (1g).
 *
 * As linhas NÃO são texto solto: saem de config/planoConfig.js (a mesma fonte
 * que os gates do app leem). Linha 1 fixa ("Treinos, refeições e ofensiva",
 * ✓ nos dois); depois
 *   • FEATURES.ads     → "Sem anúncios" (✓ onde o tier NÃO tem anúncio);
 *   • QUOTAS.*         → o número de cada coluna ("2/dia" | "6/dia");
 *   • FEATURES.*       → ✓ onde LIBERADO, traço onde BLOQUEADO.
 * Linha igual nos dois planos não entra (não é diferença de plano), e chave
 * sem rótulo conhecido também não — nunca aparece um nome cru de config nem
 * um benefício que o config não sustenta.
 *
 * O planoConfig do client não tem multiplicador de XP (GAMIFICACAO.XP_MULT
 * vive só no servidor), então essa linha não existe aqui.
 */

import React, { useMemo } from 'react';
import haptics from '../../design/feedback/haptics';
import PainelPremium from './PainelPremium';
import PremiumTopBar from './PremiumTopBar';
import { QUOTAS, FEATURES, JANELAS, TIERS } from '../../config/planoConfig';
import vitaApontando from '../../Assets/images/vita/vita-apontando-selo.webp';
import s from './Comparativo.module.scss';

const GRATIS = TIERS.GRATUITO;
const PREMIUM = TIERS.PREMIUM;

// Rótulo de cada chave do config que pode virar linha. Ordem = ordem na tabela.
const ROTULO_QUOTA = {
  scanfood: 'Fotos de refeição com IA',
  scanbody: 'Análises corporais com IA',
  geradorTreino: 'Treinos gerados pelo VITA',
  geradorDieta: 'Dietas geradas pelo VITA',
  registroTreino: 'Registro de treinos',
};

const ROTULO_FEATURE = {
  mapaMuscularInteligente: 'Mapa muscular inteligente',
  graficosEvolucao: 'Gráficos de evolução',
};

const SUFIXO_JANELA = {
  [JANELAS.DIA_BRT]: '/dia',
  [JANELAS.ROLLING_30D]: '/mês',
  [JANELAS.ROLLING_90D]: '/trim.',
};

const CHECK = { tipo: 'check' };
const TRACO = { tipo: 'traco' };

function celulaQuota(q) {
  if (!q) return TRACO;
  if (q.limite == null) return { tipo: 'texto', texto: 'Ilimitado' };
  if (q.limite <= 0) return TRACO;
  // JANELAS.VIDA: uma vez só (não renova) — "1 vez", e não um "1" solto.
  if (JANELAS.VIDA && q.janela === JANELAS.VIDA) {
    return { tipo: 'texto', texto: `${q.limite} ${q.limite === 1 ? 'vez' : 'vezes'}` };
  }
  return { tipo: 'texto', texto: `${q.limite}${SUFIXO_JANELA[q.janela] || ''}` };
}

const mesmaCelula = (a, b) => a.tipo === b.tipo && a.texto === b.texto;

/** Linhas da tabela, derivadas do planoConfig. Exportado para teste. */
export function linhasDoComparativo() {
  const linhas = [{ chave: 'base', nome: 'Treinos, refeições e ofensiva', gratis: CHECK, premium: CHECK }];

  // `ads: true` = o tier VÊ anúncio. "Sem anúncios" é ✓ onde é false.
  const ads = FEATURES.ads;
  if (ads && ads[GRATIS] !== ads[PREMIUM]) {
    linhas.push({
      chave: 'ads',
      nome: 'Sem anúncios',
      gratis: ads[GRATIS] ? TRACO : CHECK,
      premium: ads[PREMIUM] ? TRACO : CHECK,
    });
  }

  Object.keys(ROTULO_QUOTA).forEach((chave) => {
    const q = QUOTAS[chave];
    if (!q) return;
    const gratis = celulaQuota(q[GRATIS]);
    const premium = celulaQuota(q[PREMIUM]);
    if (mesmaCelula(gratis, premium)) return;
    linhas.push({ chave, nome: ROTULO_QUOTA[chave], gratis, premium });
  });

  Object.keys(ROTULO_FEATURE).forEach((chave) => {
    const f = FEATURES[chave];
    if (!f) return;
    const gratis = f[GRATIS] === 'LIBERADO' ? CHECK : TRACO;
    const premium = f[PREMIUM] === 'LIBERADO' ? CHECK : TRACO;
    if (mesmaCelula(gratis, premium)) return;
    linhas.push({ chave, nome: ROTULO_FEATURE[chave], gratis, premium });
  });

  return linhas;
}

function Celula({ valor, premium }) {
  if (valor.tipo === 'check') {
    return (
      <svg
        className={premium ? s.checkPremium : s.checkGratis}
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={premium ? 2.8 : 2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        role="img"
        aria-label="Incluso"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  }
  if (valor.tipo === 'texto') {
    return <span className={premium ? s.numeroPremium : s.numeroGratis}>{valor.texto}</span>;
  }
  return <span className={s.traco} role="img" aria-label="Não incluso" />;
}

/**
 * A tabela Grátis × Premium sozinha, sem palco nem CTA. Também é o
 * "Comparativo em modo leitura" de Configurações › Assinatura (quem já assina
 * vê o que o plano inclui). `className` ajusta a margem de fora.
 */
export function TabelaComparativo({ className }) {
  const linhas = useMemo(() => linhasDoComparativo(), []);
  return (
    <div
      className={className ? `${s.tabela} ${className}` : s.tabela}
      role="table"
      aria-label="Comparação entre Grátis e Turbo"
    >
      <span className={s.colunaPremium} aria-hidden="true" />
      <div className={s.linhaTopo} role="row">
        <span role="columnheader" className={s.oculto}>Recurso</span>
        <span role="columnheader" className={s.rotuloGratis}>GRÁTIS</span>
        <span role="columnheader" className={s.celula}>
          <span className={s.seloPremium}>TURBO</span>
        </span>
      </div>
      {linhas.map((l) => (
        <div key={l.chave} className={s.linha} role="row">
          <span role="rowheader" className={s.nome}>{l.nome}</span>
          <span role="cell" className={s.celula}>
            <Celula valor={l.gratis} />
          </span>
          <span role="cell" className={s.celula}>
            <Celula valor={l.premium} premium />
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Comparativo({ trialElegivel = true, onContinuar, onFechar, onVoltar }) {
  const continuar = () => {
    haptics.light();
    onContinuar?.();
  };

  const rotuloCta = !trialElegivel
    ? 'Ver planos'
    : 'Assinar Turbo por R$0,00';

  return (
    <PainelPremium rotulo="Grátis e Turbo" brilho="topo" onFechar={onFechar} onVoltar={onVoltar}>
      <PremiumTopBar onFechar={onFechar} onVoltar={onVoltar} />

      <div className={s.corpo}>
        <div className={s.cabeca}>
          <h1 className={s.titulo}>Tudo liberado pra você ir mais longe</h1>
          <img className={s.vita} src={vitaApontando} alt="" />
        </div>

        <TabelaComparativo />
      </div>

      <div className={s.rodape}>
        <button type="button" className={s.cta} onClick={continuar}>
          {rotuloCta}
        </button>
      </div>
    </PainelPremium>
  );
}
